import React, { useState, useCallback } from 'react';
import Code from '../../Code/Code';
import EmbedCodeCopyButton from '../../EmbedCodeCopyButton/EmbedCodeCopyButton';
import sdkUrls from '../../../../../const/sdkUrls';
import { UnControlled as ReactCodeMirror } from 'react-codemirror2';
import ComboBox from '../../../../components/ComboBox/ComboBox';
import getHostUrlForIntegrations from '../../../../../services/mini/getHostUrlForIntegrations';

const htmlCodeMirrorOptions = {
    lineNumbers: false,
    readOnly: true,
    mode: {
        name: 'javascript',
    },
    scrollbarStyle: null,
    cursorScrollMargin: -1000,
};

const alanObjectiveCImportCode = '@import AlanSDK;';

function getbjectiveCDefineCode() {
  return `@property (nonatomic) AlanButton* button;

@property (nonatomic) AlanText* text;
`;
}

function getObjectiveCCode(alanKey, codeMode) {
  if (codeMode === 'simple') {
    return `/// Define the project key
AlanConfig* config = [[AlanConfig alloc] initWithKey:@"${alanKey}" host:@"${getHostUrlForIntegrations()}"];
        
/// Init the Alan button
self.button = [[AlanButton alloc] initWithConfig:config];
        
/// Init the Alan text panel
self.text = [[AlanText alloc] initWithFrame:CGRectZero];
        
// Add the button and text panel to the view
[self.button setTranslatesAutoresizingMaskIntoConstraints:NO];
[self.view addSubview:self.button];
[self.text setTranslatesAutoresizingMaskIntoConstraints:NO];
[self.view addSubview:self.text];
        
/// Align the button and text panel on the view
NSDictionary* views = @{@"button" : self.button, @"text" : self.text};
NSArray* verticalButton = [NSLayoutConstraint constraintsWithVisualFormat:@"V:|-(>=0@299)-[button(64)]-40-|" options:0 metrics:nil views:views];
NSArray* horizontalButton = [NSLayoutConstraint constraintsWithVisualFormat:@"H:|-(>=0@299)-[button(64)]-20-|" options:0 metrics:nil views:views];
NSArray* verticalText = [NSLayoutConstraint constraintsWithVisualFormat: @"V:|-(>=0@299)-[text(64)]-40-|" options: 0 metrics: nil views: views];
NSArray* horizontalText = [NSLayoutConstraint constraintsWithVisualFormat: @"H:|-20-[text]-20-|" options: 0 metrics: nil views: views];
[self.view addConstraints:verticalButton];
[self.view addConstraints:horizontalButton];
[self.view addConstraints:verticalText];
[self.view addConstraints:horizontalText];
    
/// Handle commands from Alan AI Studio
self.button.onCommand = ^(NSDictionary *command) {
  NSString* commandName = [command objectForKey:@"command"];
  NSLog(@"%@", commandName);
};
`;
  } else {
    return `/// Define the project key and additional params
AlanConfig* config = [[AlanConfig alloc] initWithKey:@"${alanKey}" host:@"${getHostUrlForIntegrations()}" dataObject:@{@"data":@"your auth data if needed"}];
    
/// Init the Alan button
self.button = [[AlanButton alloc] initWithConfig:config];
    
/// Init the Alan text panel
self.text = [[AlanText alloc] initWithFrame:CGRectZero];
    
// Add the button and text panel to the view
[self.button setTranslatesAutoresizingMaskIntoConstraints:NO];
[self.view addSubview:self.button];
[self.text setTranslatesAutoresizingMaskIntoConstraints:NO];
[self.view addSubview:self.text];
    
/// Align the button and text panel on the view
NSDictionary* views = @{@"button" : self.button, @"text" : self.text};
NSArray* verticalButton = [NSLayoutConstraint constraintsWithVisualFormat:@"V:|-(>=0@299)-[button(64)]-40-|" options:0 metrics:nil views:views];
NSArray* horizontalButton = [NSLayoutConstraint constraintsWithVisualFormat:@"H:|-(>=0@299)-[button(64)]-20-|" options:0 metrics:nil views:views];
NSArray* verticalText = [NSLayoutConstraint constraintsWithVisualFormat: @"V:|-(>=0@299)-[text(64)]-40-|" options: 0 metrics: nil views: views];
NSArray* horizontalText = [NSLayoutConstraint constraintsWithVisualFormat: @"H:|-20-[text]-20-|" options: 0 metrics: nil views: views];
[self.view addConstraints:verticalButton];
[self.view addConstraints:horizontalButton];
[self.view addConstraints:verticalText];
[self.view addConstraints:horizontalText];

/// Handle commands from Alan AI Studio
self.button.onCommand = ^(NSDictionary *command) {
  NSString* commandName = [command objectForKey:@"command"];
  NSLog(@"%@", commandName);
};

/// Handle events
self.button.onEvent = ^(NSString *payload) {
  NSData* eventData = [payload dataUsingEncoding:NSUTF8StringEncoding];
  NSError* error = nil;
  NSDictionary* eventDict = [NSJSONSerialization JSONObjectWithData:eventData options:0 error:&error];
  if (error != nil) {
      return;
  }
  NSLog(@"%@", eventDict);
};

/// Handle button state
self.button.onButtonState = ^(AlanSDKButtonState state) {
  NSLog(@"%ld", (long)state);
};
`;
  }
}

function getObjectiveCApiCode() {
  return `/// Activate Alan Button programmatically
- (void)activate {
  [self.button activate];
}
  
/// Deactivate Alan Button programmatically
- (void)deactivate {
  [self.button deactivate];
}
  
/// Play any text via Alan Button
- (void)playText {
  /// Provide text as string param
  [self.button playText:@"Hi"];
}
  
/// Execute any command locally
- (void)playCommand {
  /// Provide any params
  [self.button playCommand:@{@"action":@"openSomePage"}];
}
  
/// Call projectAPI.funcName from Alan AI Studio script
- (void)callProjectApi {
  /// Provide any params
  [self.button callProjectApi:@"script::funcName" withData:@{@"data":@"your data"} callback:nil];
}
  
/// Set up a visual state
- (void)setVisualState {
  /// Provide any params
  [self.button setVisualState:@{@"data":@"your data"}];
}
`;
}


const codeModes = [
  { name: 'Simple Example', key: 'simple' },
  { name: 'Advanced Example', key: 'advanced' }
];

export function ObjectiveCTab({ alanKey }) {

    const [codeMode, setCodeMode] = useState('simple');

    const changeCodeModeCb = useCallback((codeModeObj) => {
      setCodeMode(codeModeObj.key);
    }, []);

    return <div className="embed-code-tab">
        <div className="embed-code-tab_line">
                <div className="embed-code-tab_description">
                </div>
                <div className="embed-code-tab_dash"></div>
                <div className="embed-code-tab_code">
                    <div className="embed-code-tab_not-highlighted-code">
                        <Code>
                            {
                                '\n'
                            }
                        </Code>
                    </div>
                </div>

            </div>
            <ComboBox values={codeModes}
                valueKey="key"
                valueName="name"
                classes="code-mode-combo-box light-blue-with-border"
                selectedValueId={codeMode}
                onValueSelect={changeCodeModeCb}
                renderItem={(codeModeItem) => {
                    return <div style={{ paddingLeft: '8px' }}
                        key={codeModeItem.key}>
                        {codeModeItem.name}
                    </div>;
                }} />
        <div className="embed-code-tab_line">
            <div className="embed-code-tab_description embed-code-desc4">
                XCode project setup
            </div>
            <div className="embed-code-tab_dash embed-code-desc4" />
            <div className="embed-code-tab_code text-desc">
                <div className="embed-code-tab_desc">
                    Download iOS SDK framework <a href={sdkUrls.ios}>here</a>
                    <br></br>
                    Copy framework to your project
                    <br></br>
                    Set the “Embed” option for the framework to “Embed & Sign” (“General” tab for your target).
                    <br></br>
                    Add the “Privacy – Microphone Usage Description” key and provide the key description (“Info” tab for your target, must be added to access the microphone).
                    <br></br>
                    Enable the “Audio, AirPlay, and Picture in Picture” background mode (“Signing and Capabilities” tab for your target).
                    <br></br>
                    <br></br>
                </div>
            </div>
        </div>
        <div className="embed-code-tab_line">
            <div className="embed-code-tab_description embed-code-desc5">
                Import AlanSDK framework
            </div>
            <div className="embed-code-tab_dash embed-code-desc5">
                <div className="embed-code-tab_separator" />
            </div>
            <div className="embed-code-tab_code">
                <div className="embed-code-tab_highlighted-code">
                    <Code highlighted={true} category="ios" type="code">
                        {alanObjectiveCImportCode}
                    </Code>
                    <div className="embed-code-tab_copy">
                        <EmbedCodeCopyButton code={alanObjectiveCImportCode} />
                    </div>
                </div>
            </div>
        </div>
        <div className="embed-code-tab_line">
            <div className="embed-code-tab_description embed-code-desc6">
                Define AlanButton variable
                </div>
            <div className="embed-code-tab_dash embed-code-desc6">
                <div className="embed-code-tab_separator" />
            </div>
            <div className="embed-code-tab_code">
                <div className="embed-code-tab_highlighted-code">
                    <Code highlighted={true} category="ios" type="code">
                        {getbjectiveCDefineCode()}
                    </Code>
                    <div className="embed-code-tab_copy">
                        <EmbedCodeCopyButton code={getbjectiveCDefineCode()} />
                    </div>
                </div>
            </div>
        </div>
        <div className="embed-code-tab_line">
            <div className="embed-code-tab_description embed-code-desc7">
                Setup AlanButton in viewDidLoad()
                </div>
            <div className="embed-code-tab_dash embed-code-desc7">
                <div className="embed-code-tab_separator" />
            </div>
            <div className="embed-code-tab_code">
                <div className="embed-code-tab_highlighted-code">
                    <Code highlighted={true} category="ios" type="code">
                        {getObjectiveCCode(alanKey, codeMode)}
                    </Code>
                    <div className="embed-code-tab_copy">
                        <EmbedCodeCopyButton code={getObjectiveCCode(alanKey, codeMode)} />
                    </div>
                </div>
            </div>
        </div>
        {(() => {
          if (codeMode === 'advanced') {
              return <div className="embed-code-tab_line">
                <div className="embed-code-tab_description embed-code-desc7">
                    AlanButton common API
                    </div>
                <div className="embed-code-tab_dash embed-code-desc7">
                    <div className="embed-code-tab_separator" />
                </div>
                <div className="embed-code-tab_code">
                    <div className="embed-code-tab_highlighted-code">
                        <Code highlighted={true} category="ios" type="code">
                          {getObjectiveCApiCode()}
                        </Code>
                        <div className="embed-code-tab_copy">
                            <EmbedCodeCopyButton code={getObjectiveCApiCode()} />
                        </div>
                    </div>
                </div>
            </div>;
          } else {
            return <></>;
          }
        })()}
    </div>;
}
